import { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Box, Checkbox, FormHelperText, Stack, Typography } from "@mui/material";

import { useCustomerContext } from "../../Context/CustomerContext";
import { useIsFeatureEntitled } from "../../Context/TierProvider";
import { Platform } from "../../Support/types";
import { isEMEAorNorthAmerica } from "../../Support/utils";

export const creCheckboxCloudServices = [Platform.azure, Platform.aws, Platform.gcp, Platform.cmp];

const MRR = 20000;

export const CallMeCheckbox = ({
  priority,
  cloudPlatform,
  onChange,
  bigWidth,
}: {
  priority: string;
  cloudPlatform: Platform;
  onChange: (checked: boolean) => void;
  bigWidth: boolean;
}) => {
  const { customer, contracts } = useCustomerContext();
  const { t } = useTranslation("services");
  const isMRRAbove20k = useMemo(() => {
    if (!contracts) {
      return false;
    }
    return contracts?.reduce((acc, contract) => acc + (contract.estimatedValue ?? 0), 0) / 12 > MRR;
  }, [contracts]);

  const shouldShowCheckbox = useIsFeatureEntitled("support:ticket:call-check");

  const isEnabledtoBeCalled = useMemo(() => {
    // Skip additional checks if the platform isn't in our supported list
    if (!creCheckboxCloudServices.includes(cloudPlatform)) {
      return false;
    }

    // For CMP platform, apply additional checks
    if (cloudPlatform === Platform.cmp) {
      return isMRRAbove20k && isEMEAorNorthAmerica(customer?.enrichment?.geo?.country ?? "");
    }

    // For other cloud platforms, always enabled
    return true;
  }, [cloudPlatform, isMRRAbove20k, customer?.enrichment?.geo?.country]);

  if (!isEnabledtoBeCalled || !shouldShowCheckbox || priority === "urgent") {
    return null;
  }

  const onCheckSpeakWithCre = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          my: 3,
          width: bigWidth ? 980 : "100%",
        }}
      >
        <Checkbox data-cy="detailsstep-callMe-checkbox" onChange={onCheckSpeakWithCre} />
        <Stack>
          <Typography variant="body1">
            {cloudPlatform === Platform.cmp ? t("DetailsStep.call") : t("DetailsStep.15MinCall")}
          </Typography>
          <FormHelperText>{t("DetailsStep.helperText1")}</FormHelperText>
        </Stack>
      </Box>
      {cloudPlatform === Platform.cmp && (
        <Box sx={{ pl: 5, mb: 2, mt: -1 }}>
          <FormHelperText>{t("DetailsStep.helperText2")}</FormHelperText>
        </Box>
      )}
    </>
  );
};
